/**
 * @file Reads the Invoicing tab's tile figures: "Outstanding" and "Overdue 60+"
 * from an `invoiceTotals` answer, each an amount and an invoice count, and
 * "Shipped, not invoiced" from an `orderLinesTotals` answer. Consumer:
 * `AnalyticsInvoicingPanel`. Each amount follows the granted level of its own
 * answer: invoice amounts through `analyticsInvoiceAmount`, the shipped value
 * as the credited column or its whole `line` twin at Org. A count is null when
 * its column is refused, absent or not an integer. The overdue figure follows
 * the aging model first: loading while aging loads, aging's refusal while
 * aging is not configured, since an empty due date answers overdue as 0 here.
 */
import type { Snapshot } from 'valtio';

import { analyticsAnswerFigure } from './analyticsAnswerFigure';
import { analyticsInvoiceAmount } from './analyticsInvoiceAmount';
import { analyticsRefusedColumn } from './analyticsRefusedColumn';
import { analyticsUnansweredFigure } from './analyticsUnansweredFigure';
import type {
  AnalyticsInvoiceFigure,
  AnalyticsInvoiceTotals,
  AnalyticsReceivableAging,
} from './analyticsInvoicingTab.types';
import type { AnalyticsAnswer, AnalyticsDecimalFigure } from './index.types';

type InvoiceFigures = Pick<AnalyticsInvoiceTotals, 'outstanding' | 'overdue'>;

const invoiceFigures = (
  answer: Snapshot<AnalyticsAnswer> | undefined,
): InvoiceFigures => {
  if (answer === undefined || answer.status !== 'answered') {
    const unanswered: AnalyticsInvoiceFigure = {
      amount: analyticsUnansweredFigure(answer),
      invoices: null,
    };
    return { outstanding: unanswered, overdue: unanswered };
  }
  const { result } = answer;
  const amount = analyticsInvoiceAmount(result.level);
  const [first] = result.rows;
  const invoicesOf = (column: string): number | null => {
    const cell = first?.[column];
    return analyticsRefusedColumn(result, column) !== null ||
      typeof cell !== 'number' ||
      !Number.isInteger(cell)
      ? null
      : cell;
  };
  return {
    outstanding: {
      amount: analyticsAnswerFigure(answer, amount.outstanding),
      invoices: invoicesOf('outstandingInvoices'),
    },
    overdue: {
      amount: analyticsAnswerFigure(answer, amount.overdue),
      invoices: invoicesOf('overdueInvoices'),
    },
  };
};

const shippedNotInvoiced = (
  answer: Snapshot<AnalyticsAnswer> | undefined,
): AnalyticsDecimalFigure =>
  analyticsAnswerFigure(
    answer,
    answer?.status === 'answered' && answer.result.level === 'org'
      ? 'lineShippedNotInvoicedValue'
      : 'shippedNotInvoicedValue',
  );

export const analyticsInvoiceTotals = (
  answer: Snapshot<AnalyticsAnswer> | undefined,
  aging: AnalyticsReceivableAging,
  orderTotals: Snapshot<AnalyticsAnswer> | undefined,
): AnalyticsInvoiceTotals => {
  const figures = invoiceFigures(answer);
  const isAgingUnknown =
    aging.status === 'loading' ||
    (aging.status === 'refused' &&
      aging.answer.status === 'refused' &&
      aging.answer.refusal.refusal === 'notConfigured');
  return {
    ...figures,
    overdue: isAgingUnknown
      ? { amount: aging, invoices: null }
      : figures.overdue,
    shippedNotInvoiced: shippedNotInvoiced(orderTotals),
  };
};
